import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Button,
  Badge
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ClearAllIcon from '@material-ui/icons/ClearAll';

const useStyles = makeStyles((theme) => ({
  alertsContainer: {
    backgroundColor: '#fff',
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    boxShadow: theme.shadows[2]
  },
  alertsHeader: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  alertsTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.primary.dark
  },
  alertsList: {
    maxHeight: 300,
    overflow: 'auto',
    padding: 0
  },
  alertItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none'
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  errorItem: {
    borderLeft: `4px solid ${theme.palette.error.main}`
  },
  warningItem: {
    borderLeft: `4px solid ${theme.palette.warning.main}`
  },
  infoItem: {
    borderLeft: `4px solid ${theme.palette.info.main}`
  },
  alertIcon: {
    minWidth: 40
  },
  errorIcon: {
    color: theme.palette.error.main
  },
  warningIcon: {
    color: theme.palette.warning.main
  },
  infoIcon: {
    color: theme.palette.info.main
  },
  alertTime: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.75rem'
  },
  crewChip: {
    marginRight: theme.spacing(1),
    fontWeight: 500
  },
  summaryAlert: {
    margin: theme.spacing(2),
    '& .MuiAlert-icon': {
      fontSize: '1.5rem'
    }
  },
  expandButton: {
    transition: 'transform 0.3s ease'
  },
  expanded: {
    transform: 'rotate(180deg)'
  },
  badge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.error.main,
      color: '#fff'
    }
  }
}));

const AlertsPanel = ({ alerts = [] }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);

  const getAlertIcon = (type) => {
    switch (type) {
      case 'error':
        return <ErrorIcon className={classes.errorIcon} />;
      case 'warning':
        return <WarningIcon className={classes.warningIcon} />;
      case 'info':
        return <InfoIcon className={classes.infoIcon} />;
      default:
        return <InfoIcon className={classes.infoIcon} />;
    }
  };

  const getAlertClass = (type) => {
    switch (type) {
      case 'error':
        return classes.errorItem;
      case 'warning':
        return classes.warningItem;
      case 'info':
        return classes.infoItem;
      default:
        return '';
    }
  };

  const formatTime = (timestamp) => {
    const mins = Math.floor((Date.now() - timestamp) / 60000);
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${mins % 60}m ago`;
    
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    
    const weeks = Math.floor(days / 7);
    if (weeks === 1) return '1 week ago';
    if (weeks < 4) return `${weeks} weeks ago`;
    
    const months = Math.floor(days / 30);
    if (months === 1) return '1 month ago';
    if (months < 12) return `${months} months ago`;
    
    return 'Long time ago';
  };

  const criticalAlerts = alerts.filter(a => a.type === 'error').length;
  const hasAlerts = alerts.length > 0;

  if (!hasAlerts) {
    return (
      <Alert severity="success" className={classes.summaryAlert}>
        <AlertTitle>All Systems Normal</AlertTitle>
        All crews are operating within expected parameters.
      </Alert>
    );
  }

  return (
    <Paper className={classes.alertsContainer}>
      <Box className={classes.alertsHeader}>
        <Typography variant="h6" className={classes.alertsTitle}>
          <Badge badgeContent={alerts.length} color="error" className={classes.badge}>
            <NotificationsActiveIcon />
          </Badge>
          Active Alerts
          {criticalAlerts > 0 && (
            <Chip
              label={`${criticalAlerts} Critical`}
              size="small"
              color="secondary"
              style={{ marginLeft: 8 }}
            />
          )}
        </Typography>
        <Box>
          <Button
            size="small"
            startIcon={<ClearAllIcon />}
            style={{ marginRight: 8 }}
          >
            Clear All
          </Button>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            className={`${classes.expandButton} ${expanded ? classes.expanded : ''}`}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>
      
      <Collapse in={expanded}>
        <List className={classes.alertsList}>
          {alerts.map((alert) => (
            <ListItem
              key={alert.id}
              className={`${classes.alertItem} ${getAlertClass(alert.type)}`}
            >
              <ListItemIcon className={classes.alertIcon}>
                {getAlertIcon(alert.type)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box>
                    <Chip
                      label={alert.crewName}
                      size="small"
                      color="primary"
                      className={classes.crewChip}
                    />
                    {alert.message}
                  </Box>
                }
                secondary={
                  <Box className={classes.alertTime}>
                    <AccessTimeIcon style={{ fontSize: 14 }} />
                    {formatTime(alert.timestamp)}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Button size="small" color="primary">
                  View
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
};

export default AlertsPanel;